
import { createContext, ReactNode, useContext, useState } from 'react';
import api from '../services/api';

interface FinishedProductOrder {
    id: string;
    input_order_id: string;
    notes: string;
}

interface FinishedProductWithExternalProviderItem {
    product: string;
    unit_price: number;
    amount: number;
    cost_category: string;
}

interface FinishedProductWithExternalProviderParcel {
    number: number;
    value: number;
    expiration_date: string;
    payment_date?: string;
    status: string;
}

interface FinishedProductOrderWithExternalProvider {
    id: string;
    items: FinishedProductWithExternalProviderItem[];
    supplier: string;
    payment_type: string;
    parcels: FinishedProductWithExternalProviderParcel[];
    notes: string;
    subsidiary: string;
    harvest: number;
    discount: number;
}

interface FinishedProductOrderWithoutRawMaterial {
    id: string;
    subsidiary: string;
    harvest: number;
}

interface FinishedProductOrdersRequest {
    startDate: string;
    endDate: string;
    trip?: string | string[];
    boat?: string | string[];
    harvest: string;
    subsidiary?: string | string[];
    costCenter?: string | string[];
    pages: number;
    limit: number;
}

interface CostParcel {
    id: string;
    finished_product_item_cost: string;
    number: number;
    value: number;
    expiration_date: string;
    payment_date: string;
    status: string;
}
interface FinishedProductOrderCost {
    id: string;
    product: string;
    amount: number;
    unit_price: number;
    cost_category: string;
    supplier: string;
    finished_product_order: string;
    payment_type: string;
    discount: number;
    notes: string;
    parcels: CostParcel[];
    sale_id: string;
}

interface BeneficiationItemParcel {
    id: string;
    finished_product_item_sale: string;
    number: number;
    value: number;
    expiration_date: string;
    payment_date: string;
    status: string;
}

interface BeneficiationItem {
    id: string;
    finished_product_beneficiation_item: string;
    unit_price: number;
    raw_material_item: string;
    raw_material_name: string;
    raw_material_amount: number;
    finished_product: string;
    finished_product_name: string;
    produced_amount: number;
    weakfish: number;
    total_value: number;
    withdrawn_amount: number;
}

interface FinishedProductOrderBeneficiationItem {
    id: string;
    finished_product_order: string;
    beneficiation_company: string;
    batch: string;
    items: BeneficiationItem[];
    parcels: BeneficiationItemParcel[];
}

interface SaleParcel {
    id: string;
    finished_product_item_sale: string;
    number: number;
    value: number;
    expiration_date: string;
    payment_date: string;
    status: string;
}
interface FinishedProductSale {
    id: string;
    dt_sale: string;
    provider: string;
    seller: string;
    finished_product_order_item: string;
    amount: number;
    unit_price: number;
    coast_category: string;
    payment_type: string;
    discount: number;
    notes: string;
    parcels: SaleParcel[];
}

interface FinishedProductReserve {
    id: string;
    dt_sale: string;
    provider: string;
    seller: string;
    finished_product_order_item: string;
    amount: number;
    unit_price: number;
    shipment_id: string;
    notes: string;
}

interface FinishedProductSaleFromReserve {
    id: string;
    dt_sale: string;
    provider: string;
    seller: string;
    reserve: string;
    amount: number;
    unit_price: number;
    coast_category: string;
    payment_type: string;
    discount: number;
    notes: string;
    parcels: SaleParcel[];
}
interface FinishedProductOrderPurchase {
    id: string;
    product: string;
    amount: number;
    unit_price: number;
    cost_category: string;
    supplier: string;
    payment_type: string;
    discount: number;
    notes: string;
    subsidiary: string;
    purchaseDate: string;
    parcels: CostParcel[];
}

interface ResaleItem {
    unit_price: number;
    finished_product: string;
    produced_amount: number;
}

interface ResaleItemParcel {
    number: number;
    value: number;
    expiration_date: string;
    payment_date: string;
    status: string;
}

interface FinishedProductOrderResaleItem {
    id: string;
    purchase_date: string;
    finished_product_order: string;
    supplier: string;
    items: ResaleItem[];
    payment_type: string;
    discount: number;
    parcels: ResaleItemParcel[];
    notes: string;
}

type FinishedProductOrderInput = Omit<FinishedProductOrder, 'id'>
type FinishedProductOrderWithExternalProviderInput = Omit<FinishedProductOrderWithExternalProvider, 'id'>
type FinishedProductOrderWithoutRawMaterialInput = Omit<FinishedProductOrderWithoutRawMaterial, 'id'>
type FinishedProductOrderCostInput = Omit<FinishedProductOrderCost, 'id'>
type FinishedProductOrderBeneficiationItemInput = Omit<FinishedProductOrderBeneficiationItem, 'id'>
type FinishedProductSaleInput = Omit<FinishedProductSale, 'id'>
type FinishedProductOrderPurchaseInput = Omit<FinishedProductOrderPurchase, 'id'>
type FinishedProductReserveInput = Omit<FinishedProductReserve, 'id'>
type FinishedProductSaleFromReserveInput = Omit<FinishedProductSaleFromReserve, 'id'>
type FinishedProductOrderResaleItemInput = Omit<FinishedProductOrderResaleItem, 'id'>

interface FinishedProductOrdersProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface ResponsePropsWithData {
    success: boolean;
    message: string;
    data: any;
}

interface FinishedProductOrdersFilt {
    type?: string | string[];
    costCenter?: string | string[];
    subsidiary?: string | string[];
    provider?: string | string[];
    trip?: string | string[];
    paymentType?: string | string[];
    boat?: string | string[];
    status?: string | string[];
    startDate: string;
    endDate: string;
    pages: number;
    limit: number;
}

interface ParcelsData {
    id_array: string[];
    status_array: string[];
    expiration_date_array: string[];
    payment_date_array: string[];
    total_order_value_exec_array: string[];
}

interface PurchaseFilter {
    startDate: string;
    endDate: string;
    provider: string;
    subsidiary: string;
    page: number;
    limit: number;
    is_apportined: boolean;
}

interface StockWithdrawalData {
    id: string;
    item: string;
    allocated_amount: number;
    allocated_at: string;
    recipient: string;
    sale_id: string;
}

type StockWithdrawalDataInput = Omit<StockWithdrawalData, 'id'>

interface StockOutputFilter {
    startDate: string;
    endDate: string;
    product: string;
    page: number;
    limit: number;
}

interface PaginationFilter {
    page: number;
    limit: number;
}

interface ShipmentFilter {
    isactive: string;
    buyer_name: string;
    shipment_year: string;
    pages: number;
    limit: number;
}

interface Shipment {
    id: string;
    subsidiary: string;
    shipment_year: number;
    shipment_number: number;
    buyer: string;
    notes: string;
}

interface ShipmentCost {
    id: string;
    cost_category: string;
    supplier: string;
    shipment: string;
    allocation_criteria: string;
    payment_type: string;
    discount: number;
    notes: string;
    parcels: CostParcel[];
    purchase_date: string;
}

type ShipmentInput = Omit<Shipment, 'id'>
type ShipmentCostInput = Omit<ShipmentCost, 'id'>

interface ExpensesFilter {
    startDate: string;
    endDate: string;
    supplier: string;
    subsidiary: string;
    pages: number;
    limit: number;
}

interface ExpenseItem {
    number: number,
    product: string;
    amount: number;
    unit_price: number;
    cost_category: string;
}
interface ExpenseParcel {
    number: number;
    value: number;
    expiration_date: string;
    payment_date: string;
    status: string;
}
interface Expense {
    id: string;
    subsidiary: string;
    supplier: string;
    discount: number;
    notes: string;
    payment_type: string;
    purchase_date: string;
    items: ExpenseItem[];
    parcels: ExpenseParcel[];
}

type ExpenseInput = Omit<Expense, 'id'>

interface SalaryExpensesFilter {
    startDate: string;
    endDate: string;
    subsidiary?: string | string[];
    status?: string | string[];
    page: number;
    limit: number;
}

interface SalaryExpenseItem {
    employee: string;
    unit_price: number;
    amount: number;
    cost_category: string;
}
interface SalaryExpenseParcel {
    finished_product_order_salary_expense?: string;
    number: number;
    value: number;
    expiration_date: string;
    payment_date: string;
    status: string;
}
interface SalaryExpense {
    salary_expense_id: string;
    subsidiary: string;
    payment_type: string;
    discount: number;
    status: string;
    begin_date: string;
    end_date: string;
    salary_month: string;
    notes: string;
    parcels: SalaryExpenseParcel[];
    items: SalaryExpenseItem[];
}

type SalaryExpenseInput = Omit<SalaryExpense, 'salary_expense_id'>

interface FinishedProductOrdersContextData {
    finishedProductStockChanged: number;
    setFinishedProductStockChanged: (state: number) => void;
    getFilteredFinishedProductOrders: (request: FinishedProductOrdersRequest) => Promise<ResponsePropsWithData>;
    getFinishedProductOrder: (id: string) => Promise<ResponsePropsWithData>;
    getFinishedProductOrderWithExternalProvider: (id: string) => Promise<ResponsePropsWithData>;
    createFinishedProductOrder: (newFinishedProductOrder: FinishedProductOrderInput) => Promise<ResponseProps>;
    updateFinishedProductOrder: (finishedProductOrder: FinishedProductOrder) => Promise<ResponseProps>;
    deleteFinishedProductOrder: (id: string) => Promise<ResponseProps>;
    getFinishedProductOrderCosts: (finished_product_order_id: string) => Promise<ResponsePropsWithData>;
    getFinishedProductOrderCost: (id: string) => Promise<ResponsePropsWithData>;
    createFinishedProductOrderCost: (newFinishedProductOrderCost: FinishedProductOrderCostInput) => Promise<ResponseProps>;
    updateFinishedProductOrderCost: (idFinishedProductOrderCost: string, finishedProductOrderCostInput: FinishedProductOrderCostInput) => Promise<ResponseProps>;
    deleteFinishedProductOrderCost: (id: string) => Promise<ResponseProps>;
    getFinishedProductOrderBeneficiationItems: (finished_product_order_id: string) => Promise<ResponsePropsWithData>;
    getFinishedProductOrderBeneficiationItem: (id: string) => Promise<ResponsePropsWithData>;
    createFinishedProductOrderBeneficiationItem: (newFinishedProductOrderBeneficiationItemInput: FinishedProductOrderBeneficiationItemInput) => Promise<ResponseProps>;
    updateFinishedProductOrderBeneficiationItem: (idFinishedProductOrderBeneficiationItem: string, finishedProductOrderBeneficiationItemInput: FinishedProductOrderBeneficiationItemInput) => Promise<ResponseProps>;
    deleteFinishedProductOrderBeneficiationItem: (id: string) => Promise<ResponseProps>;
    getFinishedProductOrderSales: (finished_product_order_id: string) => Promise<ResponsePropsWithData>;
    getFinishedProductOrderSale: (id: string) => Promise<ResponsePropsWithData>;
    createFinishedProductSale: (newFinishedProductSaleInput: FinishedProductSaleInput) => Promise<ResponseProps>;
    updateFinishedProductSale: (idFinishedProductSale: string, finishedProductSaleInput: FinishedProductSaleInput) => Promise<ResponseProps>;
    deleteFinishedProductSale: (id: string) => Promise<ResponseProps>;
    getFinishedProductOrdersFilt: (filter: FinishedProductOrdersFilt) => Promise<ResponsePropsWithData>;
    updateCostParcelsPaymentData: (costParcelsData: ParcelsData) => Promise<ResponseProps>;
    updateSaleParcelsPaymentData: (saleParcelsData: ParcelsData) => Promise<ResponseProps>;
    getFinishedProductOrderPurchases: (filter: PurchaseFilter) => Promise<ResponsePropsWithData>;
    getFinishedProductOrderPurchase: (id: string) => Promise<ResponsePropsWithData>;
    createFinishedProductOrderPurchase: (newFinishedProductOrderPurchase: FinishedProductOrderPurchaseInput) => Promise<ResponseProps>;
    updateFinishedProductOrderPurchase: (idFinishedProductOrderPurchase: string, finishedProductOrderPurchaseInput: FinishedProductOrderPurchaseInput) => Promise<ResponseProps>;
    deleteFinishedProductOrderPurchase: (id: string) => Promise<ResponseProps>;
    updatePurchaseParcelsPaymentData: (purchaseParcelsData: ParcelsData) => Promise<ResponseProps>;
    getFinishedProductOrderStockItems: () => Promise<ResponsePropsWithData>;
    getFinishedProductOrderStockWithdrawals: (id: string) => Promise<ResponsePropsWithData>;
    createFinishedProductOrderStockWithdrawal: (stockWithdrawal: StockWithdrawalDataInput) => Promise<ResponseProps>;
    deleteFinishedProductOrderStockWithdrawal: (id: string) => Promise<ResponseProps>;
    getFinishedProductOrderStockOutputs: (filter: StockOutputFilter) => Promise<ResponsePropsWithData>;
    getFinishedProductOrderStockHistory: (filter: PaginationFilter) => Promise<ResponsePropsWithData>;
    getAllFinishedProductStock: () => Promise<ResponsePropsWithData>;
    getFinishedProductStockByBeneficiationCompany: () => Promise<ResponsePropsWithData>;
    getFinishedProductProductionByHarvest: (harvest: string) => Promise<ResponsePropsWithData>;
    getFinishedProductResultsByHarvest: (harvest: string) => Promise<ResponsePropsWithData>;
    getFilteredShipments: (filter: ShipmentFilter) => Promise<ResponsePropsWithData>;
    getShipment: (id: string) => Promise<ResponsePropsWithData>;
    createNewShipment: (shipment: ShipmentInput) => Promise<ResponseProps>;
    updateShipment: (shipment_id: string, shipment: ShipmentInput) => Promise<ResponseProps>;
    deleteShipment: (shipment_id: string) => Promise<ResponseProps>;
    getShipmentsByBuyer: (buyer_id: string) => Promise<ResponsePropsWithData>;
    getShipmentCosts: (shipment_id: string) => Promise<ResponsePropsWithData>;
    getShipmentCostsByFinishedProductOrder: (finished_product_order_id: string) => Promise<ResponsePropsWithData>;
    getShipmentCost: (id: string) => Promise<ResponsePropsWithData>;
    createShipmentCost: (newShipmentCost: ShipmentCostInput) => Promise<ResponseProps>;
    updateShipmentCost: (id: string, shipmentCostInput: ShipmentCostInput) => Promise<ResponseProps>;
    deleteShipmentCost: (id: string) => Promise<ResponseProps>;
    getFinishedProductOrderReserves: (finished_product_order_id: string) => Promise<ResponsePropsWithData>;
    getFinishedProductOrderReserve: (id: string) => Promise<ResponsePropsWithData>;
    createFinishedProductReserve: (newFinishedProductReserveInput: FinishedProductReserveInput) => Promise<ResponseProps>;
    updateFinishedProductReserve: (idFinishedProductReserve: string, finishedProductReserveInput: FinishedProductReserveInput) => Promise<ResponseProps>;
    deleteFinishedProductReserve: (id: string) => Promise<ResponseProps>;
    createFinishedProductSaleFromReserve: (newFinishedProductSaleFromReserveInput: FinishedProductSaleFromReserveInput) => Promise<ResponseProps>;
    getRawMaterialListByFPO: (finished_product_order_id: string) => Promise<ResponsePropsWithData>;
    createFinishedProductOrderWithExternalProvider: (newFinishedProductOrderWithExternalProvider: FinishedProductOrderWithExternalProviderInput) => Promise<ResponseProps>;
    createFinishedProductOrderWithoutRawMaterial: (newFinishedProductOrderWithoutRawMaterial: FinishedProductOrderWithoutRawMaterialInput) => Promise<ResponseProps>;
    getFinishedProductOrderWithoutRawMaterial: (id: string) => Promise<ResponsePropsWithData>;
    getFinishedProductOrderResaleItems: (finished_product_order_id: string) => Promise<ResponsePropsWithData>;
    getFinishedProductOrderResaleItem: (id: string) => Promise<ResponsePropsWithData>;
    createFinishedProductOrderResaleItem: (newFinishedProductOrderResaleItemInput: FinishedProductOrderResaleItemInput) => Promise<ResponseProps>;
    updateFinishedProductOrderResaleItem: (idFinishedProductOrderResaleItem: string, finishedProductOrderResaleItemInput: FinishedProductOrderResaleItemInput) => Promise<ResponseProps>;
    deleteFinishedProductOrderResaleItem: (id: string) => Promise<ResponseProps>;
    getFilteredExpenses: (filter: ExpensesFilter) => Promise<ResponsePropsWithData>;
    getExpense: (id: string) => Promise<ResponsePropsWithData>;
    createExpense: (expense: ExpenseInput) => Promise<ResponseProps>;
    updateExpense: (expense_id: string, expense: ExpenseInput) => Promise<ResponseProps>;
    deleteExpense: (expense_id: string) => Promise<ResponseProps>;
    updateExpenseParcelsPaymentData: (expenseParcelsData: ParcelsData) => Promise<ResponseProps>;
    getFilteredSalaryExpenses: (filter: SalaryExpensesFilter) => Promise<ResponsePropsWithData>;
    getSalaryExpense: (id: string) => Promise<ResponsePropsWithData>;
    createSalaryExpense: (salary_expense: SalaryExpenseInput) => Promise<ResponseProps>;
    updateSalaryExpense: (salary_expense_id: string, salary_expense: SalaryExpenseInput) => Promise<ResponseProps>;
    deleteSalaryExpense: (salary_expense_id: string) => Promise<ResponseProps>;
    updateSalaryExpenseParcelsPaymentData: (salaryExpenseParcelsData: ParcelsData) => Promise<ResponseProps>;
    getFinishedProductOrdersSalaryExpenseFilt: (filter: FinishedProductOrdersFilt) => Promise<ResponsePropsWithData>;
}

const FinishedProductOrdersContext = createContext<FinishedProductOrdersContextData>(
    {} as FinishedProductOrdersContextData
);

export function FinishedProductOrdersProvider({ children }: FinishedProductOrdersProvidersProps) {
  const [finishedProductStockChanged, setFinishedProductStockChanged] = useState(0);

  async function getFilteredFinishedProductOrders(request: FinishedProductOrdersRequest) {
    let params = {
      ...request
    };

    const response = await api.get('/finished-product-orders', { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar pedidos de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrder(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-orders/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar pedido de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrderWithExternalProvider(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-order-with-external-raw-material/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar pedido de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrdersFilt(filter: FinishedProductOrdersFilt) {
    let params = {
      ...filter
    };

    const response = await api.get('/finished-product-orders-filt', { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar pedidos de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrdersSalaryExpenseFilt(filter: FinishedProductOrdersFilt) {
    let params = {
      ...filter
    };

    const response = await api.get('/finished-product-orders-salary-expense-filt', { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar despesas com remuneração em produto acabado.",
      data: []
    };
  }

  async function createFinishedProductOrder(newFinishedProductOrder: FinishedProductOrderInput) {
    const response = await api.post('/finished-product-orders', newFinishedProductOrder);

    if (response.status === 201) {
      return {
        success: true,
        message: "Pedido de produto acabado adicionado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar pedido de produto acabado."
    };
  }

  async function createFinishedProductOrderWithExternalProvider(newFinishedProductOrderWithExternalProvider: FinishedProductOrderWithExternalProviderInput) {
    const response = await api.post('/finished-product-order-with-external-raw-material', newFinishedProductOrderWithExternalProvider);

    if (response.status === 201) {
      return {
        success: true,
        message: "Pedido de produto acabado com fornecedor externo adicionado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar pedido de produto acabado com fornecedor externo."
    };
  }

  async function updateFinishedProductOrder(finishedProductOrder: FinishedProductOrder) {
    const response = await api.put('/finished-product-orders', finishedProductOrder);

    if (response.status === 200) {
      return {
        success: true,
        message: "Pedido de produto acabado editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar pedido de produto acabado."
    };
  }

  async function deleteFinishedProductOrder(id: string) {
    const response = await api.delete(`/finished-product-orders/${id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Pedido de produto acabado removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover pedido de produto acabado."
    };
  }

  async function getFinishedProductOrderCosts(finished_product_order_id: string) {
    let params = {
      finished_product_order_id
    };

    const response = await api.get(`/finished-product-orders/${finished_product_order_id}/costs`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar custos de pedidos de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrderCost(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-order-costs/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar custo de pedido de produto acabado.",
      data: []
    };
  }

  async function createFinishedProductOrderCost(newFinishedProductOrderCost: FinishedProductOrderCostInput) {
    const response = await api.post('/finished-product-order-costs', newFinishedProductOrderCost);

    if (response.status === 201) {
      return {
        success: true,
        message: "Custo de pedido de produto acabado adicionado com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar custo de pedido de produto acabado."
    };
  }

  async function updateFinishedProductOrderCost(idFinishedProductOrderCost: string, finishedProductOrderCostInput: FinishedProductOrderCostInput) {
    const response = await api.put(`/finished-product-order-costs/${idFinishedProductOrderCost}`, finishedProductOrderCostInput);

    if (response.status === 200) {
      return {
        success: true,
        message: "Custo de pedido de produto acabado editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar custo de pedido de produto acabado."
    };
  }

  async function deleteFinishedProductOrderCost(id: string) {
    const response = await api.delete(`/finished-product-order-costs/${id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Custo de pedido de produto acabado removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover custo de pedido de produto acabado."
    };
  }

  async function getFinishedProductOrderBeneficiationItems(finished_product_order_id: string) {
    let params = {
      finished_product_order_id
    };

    const response = await api.get(`/finished-product-orders/${finished_product_order_id}/items`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar itens de beneficiamento de pedido de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrderBeneficiationItem(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-order-items/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar item de beneficiamento de pedido de produto acabado.",
      data: []
    };
  }

  async function createFinishedProductOrderBeneficiationItem(newFinishedProductOrderBeneficiationItemInput: FinishedProductOrderBeneficiationItemInput) {
    const response = await api.post('/finished-product-order-items', newFinishedProductOrderBeneficiationItemInput);

    if (response.status === 201) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Item de beneficiamento de produto acabado adicionado com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar item de beneficiamento de pedido de produto acabado."
    };
  }

  async function updateFinishedProductOrderBeneficiationItem(idFinishedProductOrderBeneficiationItem: string, finishedProductOrderBeneficiationItemInput: FinishedProductOrderBeneficiationItemInput) {
    const response = await api.put(`/finished-product-order-items/${idFinishedProductOrderBeneficiationItem}`, finishedProductOrderBeneficiationItemInput);

    if (response.status === 200) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Item de beneficiamento de pedido de produto acabado editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar item de beneficiamento de pedido de produto acabado."
    };
  }

  async function deleteFinishedProductOrderBeneficiationItem(id: string) {
    const response = await api.delete(`/finished-product-order-items/${id}`);

    if (response.status === 200) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Item de beneficiamento de pedido de produto acabado removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover item de beneficiamento de pedido de produto acabado."
    };
  }

  async function getFinishedProductOrderSales(finished_product_order_id: string) {
    let params = {
      finished_product_order_id
    };

    const response = await api.get(`/finished-product-orders/${finished_product_order_id}/sales`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar vendas de pedido de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrderSale(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-order-sales/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar venda de pedido de produto acabado.",
      data: []
    };
  }

  async function createFinishedProductSale(newFinishedProductSaleInput: FinishedProductSaleInput) {
    const response = await api.post('/finished-product-order-sales', newFinishedProductSaleInput);

    if (response.status === 201) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Venda de produto acabado adicionada com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar venda de produto acabado."
    };
  }

  async function updateFinishedProductSale(idFinishedProductSale: string, finishedProductSaleInput: FinishedProductSaleInput) {
    const response = await api.put(`/finished-product-order-sales/${idFinishedProductSale}`, finishedProductSaleInput);

    if (response.status === 200) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Venda de produto acabado editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar venda de produto acabado."
    };
  }

  async function deleteFinishedProductSale(id: string) {
    const response = await api.delete(`/finished-product-order-sales/${id}`);

    if (response.status === 200) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Venda de produto acabado removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover venda de produto acabado."
    };
  }

  async function updateCostParcelsPaymentData(costParcelsData: ParcelsData) {
    const response = await api.put('/finished-product-order-costs-parcels', costParcelsData);

    if (response.status === 200) {
      return {
        success: true,
        message: "Dados de pagamento de parcelas de custo de pedido de produto acabado alterado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao alterar dados de pagamento de parcelas de custo de pedido de produto acabado."
    };
  }

  async function updateSaleParcelsPaymentData(saleParcelsData: ParcelsData) {
    const response = await api.put('/finished-product-order-sales-parcels', saleParcelsData);

    if (response.status === 200) {
      return {
        success: true,
        message: "Dados de pagamento de parcelas de venda de pedido de produto acabado alterado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao alterar dados de pagamento de parcelas de venda de pedido de produto acabado."
    };
  }

  async function getFinishedProductOrderPurchase(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-order-purchases/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar compra para pedido de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrderPurchases(filter: PurchaseFilter) {
    let params = {
      ...filter
    };

    const response = await api.get(`/finished-product-order-purchases`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar compras para pedidos de produto acabado.",
      data: []
    };
  }

  async function createFinishedProductOrderPurchase(newFinishedProductOrderpurchase: FinishedProductOrderPurchaseInput) {
    const response = await api.post('/finished-product-order-purchases', newFinishedProductOrderpurchase);

    if (response.status === 201) {
      return {
        success: true,
        message: "Compra para pedido de produto acabado adicionado com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar compra para pedido de produto acabado."
    };
  }

  async function updateFinishedProductOrderPurchase(idFinishedProductOrderPurchase: string, finishedProductOrderPurchaseInput: FinishedProductOrderPurchaseInput) {
    const response = await api.put(`/finished-product-order-purchases/${idFinishedProductOrderPurchase}`, finishedProductOrderPurchaseInput);

    if (response.status === 200) {
      return {
        success: true,
        message: "Compra para pedido de produto acabado editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar compra para pedido de produto acabado."
    };
  }

  async function deleteFinishedProductOrderPurchase(id: string) {
    const response = await api.delete(`/finished-product-order-purchases/${id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Compra para pedido de produto acabado removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover compra para pedido de produto acabado."
    };
  }

  async function updatePurchaseParcelsPaymentData(purchaseParcelsData: ParcelsData) {
    const response = await api.put('/finished-product-order-purchase-parcels', purchaseParcelsData);

    if (response.status === 200) {
      return {
        success: true,
        message: "Dados de pagamento de parcelas de compra para pedido de produto acabado alterado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao alterar dados de pagamento de parcelas de compra para pedido de produto acabado."
    };
  }

  async function getFinishedProductOrderStockItems() {
    const response = await api.get(`/finished-product-order-stock-items`);

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar itens de estoque para pedidos de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrderStockWithdrawals(id: string) {
    const response = await api.get(`/finished-product-orders/${id}/stock-withdrawal`);

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar itens de estoque para pedidos de produto acabado.",
      data: []
    };
  }

  async function createFinishedProductOrderStockWithdrawal(stockWithdrawal: StockWithdrawalDataInput) {
    const response = await api.post('/finished-product-order-stock-withdrawal', stockWithdrawal);

    if (response.status === 201) {
      return {
        success: true,
        message: "Retirada de item de estoque para pedido de produto acabado adicionada com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao retirar item de estoque para pedido de produto acabado."
    };
  }

  async function deleteFinishedProductOrderStockWithdrawal(id: string) {
    const response = await api.delete(`/finished-product-order-stock-withdrawal/${id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Item de estoque para pedido de produto acabado removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover item de estoque para pedido de produto acabado."
    };
  }

  async function getFinishedProductOrderStockOutputs(filter: StockOutputFilter) {
    let params = {
      ...filter
    };

    const response = await api.get(`/finished-product-order-stock-withdrawal`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar movimentações de item do estoque para pedidos de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrderStockHistory(filter: PaginationFilter) {
    let params = {
      ...filter
    };

    const response = await api.get(`/finished-product-order-stock-withdrawal-logs`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar o histórico do estoque para pedidos de produto acabado.",
      data: []
    };
  }

  async function getAllFinishedProductStock() {
    const response = await api.get(`/finished-product-orders/available-items`);

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar todo o estoque de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductStockByBeneficiationCompany() {
    const response = await api.get(`/finished-product-orders/available-items-by-suppliers`);

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar todo o estoque de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductProductionByHarvest(harvest: string) {
    let params = {
      harvest
    };

    const response = await api.get(`/finished-product-orders/production-summary`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar todo o estoque de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductResultsByHarvest(harvest: string) {
    let params = {
      harvest
    };

    const response = await api.get(`/finished-product-orders/result-summary`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar todo o estoque de produto acabado.",
      data: []
    };
  }

  async function getFilteredShipments(filter: ShipmentFilter) {
    let params = {
      ...filter
    };

    const response = await api.get('/finished-product-shipments', { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar embarques de produto acabado.",
      data: []
    };
  }

  async function getShipmentsByBuyer(buyer_id: string) {
    let params = {
      buyer_id
    };

    const response = await api.get('/open-finished-product-shipments', { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar embarques de produto acabado por comprador.",
      data: []
    };
  }

  async function getShipment(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-shipments/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar embarque de produto acabado por id.",
      data: []
    };
  }

  async function createNewShipment(shipment: ShipmentInput) {
    const response = await api.post('/finished-product-shipments', shipment);

    if (response.status === 201) {
      return {
        success: true,
        message: "Embarque adicionado com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao cadastrar novo embarque."
    };
  }

  async function updateShipment(shipment_id: string, shipment: ShipmentInput) {
    const response = await api.put(`/finished-product-shipments/${shipment_id}`, shipment);

    if (response.status === 200) {
      return {
        success: true,
        message: "Embarque atualizado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao atualizar embarque."
    };
  }

  async function deleteShipment(shipment_id: string) {
    const response = await api.delete(`/finished-product-shipments/${shipment_id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Embarque removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover embarque."
    };
  }

  async function getShipmentCosts(shipment_id: string) {
    let params = {
      shipment_id
    };

    const response = await api.get(`/finished-product-shipments/${shipment_id}/costs`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar custos de embarque.",
      data: []
    };
  }

  async function getShipmentCostsByFinishedProductOrder(finished_product_order_id: string) {
    let params = {
      finished_product_order_id
    };

    const response = await api.get(`/finished-product-orders/${finished_product_order_id}/shipment-costs`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar custos de embarque a partir de um pedido de produto acabado.",
      data: []
    };
  }

  async function getShipmentCost(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-shipment-costs/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar custo de embarque.",
      data: []
    };
  }

  async function createShipmentCost(newShipmentCost: ShipmentCostInput) {
    const response = await api.post('/finished-product-shipment-costs', newShipmentCost);

    if (response.status === 201) {
      return {
        success: true,
        message: "Custo de embarque adicionado com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar custo de embarque."
    };
  }

  async function updateShipmentCost(id: string, shipmentCostInput: ShipmentCostInput) {
    const response = await api.put(`/finished-product-shipment-costs/${id}`, shipmentCostInput);

    if (response.status === 200) {
      return {
        success: true,
        message: "Custo de embarque editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar custo de embarque acabado."
    };
  }

  async function deleteShipmentCost(id: string) {
    const response = await api.delete(`/finished-product-shipment-costs/${id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Custo de embarque removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover custo de embarque acabado."
    };
  }

  async function getFinishedProductOrderReserves(finished_product_order_id: string) {
    let params = {
      finished_product_order_id
    };

    const response = await api.get(`/finished-product-orders/${finished_product_order_id}/reserves`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar reservas de venda de pedido de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrderReserve(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-order-reserves/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar reserva de venda de pedido de produto acabado.",
      data: []
    };
  }

  async function createFinishedProductReserve(newFinishedProductReserveInput: FinishedProductReserveInput) {
    const response = await api.post('/finished-product-order-reserves', newFinishedProductReserveInput);

    if (response.status === 201) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Reserva de venda de produto acabado adicionada com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar reserva de venda de produto acabado."
    };
  }

  async function updateFinishedProductReserve(idFinishedProductReserve: string, finishedProductReserveInput: FinishedProductReserveInput) {
    const response = await api.put(`/finished-product-order-reserves/${idFinishedProductReserve}`, finishedProductReserveInput);

    if (response.status === 200) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Reserva de venda de produto acabado editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar reserva de venda de produto acabado."
    };
  }

  async function deleteFinishedProductReserve(id: string) {
    const response = await api.delete(`/finished-product-order-reserves/${id}`);

    if (response.status === 200) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Reserva de venda de produto acabado removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover reserva de venda de produto acabado."
    };
  }

  async function createFinishedProductSaleFromReserve(newFinishedProductSaleFromReserveInput: FinishedProductSaleFromReserveInput) {
    const response = await api.post('/finished-product-order-sale-from-reserve', newFinishedProductSaleFromReserveInput);

    if (response.status === 201) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Venda de produto acabado a partir de um reserva adicionada com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar venda de produto acabado a partir de um reserva."
    };
  }

  async function getRawMaterialListByFPO(finished_product_order_id: string) {
    let params = {
      finished_product_order_id
    };

    const response = await api.get(`/finished-product-orders/${finished_product_order_id}/raw-material-items`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar a lista de matéria-prima de um pedido de produto acabado.",
      data: []
    };
  }

  async function createFinishedProductOrderWithoutRawMaterial(newFinishedProductOrderWithoutRawMaterialInput: FinishedProductOrderWithoutRawMaterialInput) {
    const response = await api.post('/finished-product-order-without-raw-material', newFinishedProductOrderWithoutRawMaterialInput);

    if (response.status === 201) {
      return {
        success: true,
        message: "Pedido de produto acabado para revenda adicionado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar pedido de produto acabado para revenda."
    };
  }

  async function getFinishedProductOrderWithoutRawMaterial(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-order-without-raw-material/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar pedido de produto acabado para revenda.",
      data: []
    };
  }

  async function createFinishedProductOrderResaleItem(newFinishedProductOrderResaleItemInput: FinishedProductOrderResaleItemInput) {
    const response = await api.post('/finished-product-order-resale-items', newFinishedProductOrderResaleItemInput);

    if (response.status === 201) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Item para revenda de produto acabado adicionado com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar item para revenda de produto acabado."
    };
  }

  async function updateFinishedProductOrderResaleItem(idFinishedProductOrderResaleItem: string, finishedProductOrderResaleItemInput: FinishedProductOrderResaleItemInput) {
    const response = await api.put(`/finished-product-order-resale-items/${idFinishedProductOrderResaleItem}`, finishedProductOrderResaleItemInput);

    if (response.status === 200) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Item de recenda de produto acabado editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar item de revenda de pedido de produto acabado."
    };
  }

  async function getFinishedProductOrderResaleItems(finished_product_order_id: string) {
    let params = {
      finished_product_order_id
    };

    const response = await api.get(`/finished-product-orders/${finished_product_order_id}/resale-items`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar itens de revenda de pedido de produto acabado.",
      data: []
    };
  }

  async function getFinishedProductOrderResaleItem(id: string) {
    let params = {
      id
    };

    const response = await api.get(`/finished-product-order-resale-items/${id}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar item de beneficiamento de pedido de produto acabado.",
      data: []
    };
  }

  async function deleteFinishedProductOrderResaleItem(id: string) {
    const response = await api.delete(`/finished-product-order-resale-items/${id}`);

    if (response.status === 200) {
      setFinishedProductStockChanged((prev) => prev + 1);

      return {
        success: true,
        message: "Item de revenda de pedido de produto acabado removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover item de revenda de pedido de produto acabado."
    };
  }

  async function getFilteredExpenses(filter: ExpensesFilter) {
    let params = {
      ...filter
    };

    const response = await api.get('/finished-product-order-expenses', { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar despesas de produto acabado.",
      data: []
    };
  }

  async function getExpense(expense_id: string) {
    const response = await api.get(`/finished-product-order-expenses/${expense_id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar despesa de produto acabado por id.",
      data: []
    };
  }

  async function createExpense(expense: ExpenseInput) {
    const response = await api.post('/finished-product-order-expenses', expense);

    if (response.status === 201) {
      return {
        success: true,
        message: "Despesa adicionada com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao cadastrar nova despesa."
    };
  }

  async function updateExpense(expense_id: string, expense: ExpenseInput) {
    const response = await api.put(`/finished-product-order-expenses/${expense_id}`, expense);

    if (response.status === 200) {
      return {
        success: true,
        message: "Despesa atualizada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao atualizar despesa."
    };
  }

  async function deleteExpense(expense_id: string) {
    const response = await api.delete(`/finished-product-order-expenses/${expense_id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Despesa removida com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover despesa."
    };
  }

  async function updateExpenseParcelsPaymentData(expenseParcelsData: ParcelsData) {
    const response = await api.put('/finished-product-order-expenses-parcels', expenseParcelsData);

    if (response.status === 200) {
      return {
        success: true,
        message: "Dados de pagamento de parcelas de despesa com produto acabado alterado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao alterar dados de pagamento de parcelas de despesa com produto acabado."
    };
  }

  async function getFilteredSalaryExpenses(filter: SalaryExpensesFilter) {
    let params = {
      ...filter
    };

    const response = await api.get('/finished-product-order-salary-expenses', { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar despesas com remuneração em produto acabado.",
      data: []
    };
  }

  async function getSalaryExpense(salary_expense_id: string) {
    const response = await api.get(`/finished-product-order-salary-expenses/${salary_expense_id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar despesa com remuneração em produto acabado por id.",
      data: []
    };
  }

  async function createSalaryExpense(salary_expense: SalaryExpenseInput) {
    const response = await api.post('/finished-product-order-salary-expenses', salary_expense);

    if (response.status === 201) {
      return {
        success: true,
        message: "Despesa com remuneração adicionada com sucesso!",
        id: response.data.id
      };
    }
    return {
      success: false,
      message: "Erro ao cadastrar nova despesa com remuneração."
    };
  }

  async function updateSalaryExpense(salary_expense_id: string, salary_expense: SalaryExpenseInput) {
    const response = await api.put(`/finished-product-order-salary-expenses/${salary_expense_id}`, salary_expense);

    if (response.status === 200) {
      return {
        success: true,
        message: "Despesa com remuneração atualizada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao atualizar despesa com remuneração."
    };
  }

  async function deleteSalaryExpense(salary_expense_id: string) {
    const response = await api.delete(`/finished-product-order-salary-expenses/${salary_expense_id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Despesa com remuneração removida com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover despesa com remuneração."
    };
  }

  async function updateSalaryExpenseParcelsPaymentData(salaryExpenseParcelsData: ParcelsData) {
    const response = await api.put('/finished-product-order-salary-expenses-parcels', salaryExpenseParcelsData);

    if (response.status === 200) {
      return {
        success: true,
        message: "Dados de pagamento de parcelas de despesa com remuneração em produto acabado alterado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao alterar dados de pagamento de parcelas de despesa com remuneração em produto acabado."
    };
  }

  return (
    <FinishedProductOrdersContext.Provider value={{
      finishedProductStockChanged,
      setFinishedProductStockChanged,
      getFilteredFinishedProductOrders,
      getFinishedProductOrder,
      getFinishedProductOrdersFilt,
      createFinishedProductOrder,
      createFinishedProductOrderWithExternalProvider,
      createFinishedProductOrderWithoutRawMaterial,
      getFinishedProductOrderWithoutRawMaterial,
      updateFinishedProductOrder,
      deleteFinishedProductOrder,
      createFinishedProductOrderCost,
      updateFinishedProductOrderCost,
      deleteFinishedProductOrderCost,
      getFinishedProductOrderCosts,
      getFinishedProductOrderCost,
      getFinishedProductOrderBeneficiationItems,
      getFinishedProductOrderBeneficiationItem,
      createFinishedProductOrderBeneficiationItem,
      updateFinishedProductOrderBeneficiationItem,
      deleteFinishedProductOrderBeneficiationItem,
      getFinishedProductOrderSales,
      getFinishedProductOrderSale,
      createFinishedProductSale,
      updateFinishedProductSale,
      deleteFinishedProductSale,
      updateCostParcelsPaymentData,
      updateSaleParcelsPaymentData,
      getFinishedProductOrderPurchases,
      getFinishedProductOrderPurchase,
      createFinishedProductOrderPurchase,
      updateFinishedProductOrderPurchase,
      deleteFinishedProductOrderPurchase,
      updatePurchaseParcelsPaymentData,
      getFinishedProductOrderStockItems,
      getFinishedProductOrderStockWithdrawals,
      createFinishedProductOrderStockWithdrawal,
      deleteFinishedProductOrderStockWithdrawal,
      getFinishedProductOrderStockOutputs,
      getFinishedProductOrderStockHistory,
      getAllFinishedProductStock,
      getFinishedProductStockByBeneficiationCompany,
      getFinishedProductProductionByHarvest,
      getFinishedProductResultsByHarvest,
      getFilteredShipments,
      getShipment,
      createNewShipment,
      updateShipment,
      deleteShipment,
      getShipmentsByBuyer,
      getShipmentCosts,
      getShipmentCostsByFinishedProductOrder,
      getShipmentCost,
      createShipmentCost,
      updateShipmentCost,
      deleteShipmentCost,
      getFinishedProductOrderReserves,
      getFinishedProductOrderReserve,
      createFinishedProductReserve,
      updateFinishedProductReserve,
      deleteFinishedProductReserve,
      createFinishedProductSaleFromReserve,
      getRawMaterialListByFPO,
      getFinishedProductOrderWithExternalProvider,
      createFinishedProductOrderResaleItem,
      updateFinishedProductOrderResaleItem,
      getFinishedProductOrderResaleItems,
      getFinishedProductOrderResaleItem,
      deleteFinishedProductOrderResaleItem,
      getFilteredExpenses,
      getExpense,
      createExpense,
      updateExpense,
      deleteExpense,
      updateExpenseParcelsPaymentData,
      getFilteredSalaryExpenses,
      getSalaryExpense,
      createSalaryExpense,
      updateSalaryExpense,
      deleteSalaryExpense,
      updateSalaryExpenseParcelsPaymentData,
      getFinishedProductOrdersSalaryExpenseFilt
    }}>
      {children}
    </FinishedProductOrdersContext.Provider>
  );
}

export function UseFinishedProductOrders() {
  return useContext(FinishedProductOrdersContext);
}
